/**
 * The ONE place web/ asks "what kind of device is this?".
 *
 *   - Layout changes (stacking, spacing, columns): use Tailwind breakpoints in
 *     CSS (mobile-first: base = phone, `md:` / `lg:` add desktop). No JS.
 *   - Structure changes (render a different component, hide a feature): use
 *     useViewport() / <Adaptive> / route `surfaces` (see surfaces.tsx).
 *
 * Breakpoints MUST match Tailwind's (md = 768px, lg = 1024px). Raw
 * matchMedia / innerWidth elsewhere fails scripts/check-conventions.ts.
 */

import { useSyncExternalStore } from "react";

export const BREAKPOINTS = { md: 768, lg: 1024 } as const;

export type Surface = "mobile" | "tablet" | "desktop";

const QUERIES = {
  md: `(min-width: ${BREAKPOINTS.md}px)`,
  lg: `(min-width: ${BREAKPOINTS.lg}px)`,
  coarse: "(pointer: coarse)",
} as const;

function matches(query: string): boolean {
  // No matchMedia (SSR, some test envs): assume desktop with a fine pointer.
  return globalThis.matchMedia?.(query).matches ?? query !== QUERIES.coarse;
}

function subscribe(onChange: () => void): () => void {
  const lists = Object.values(QUERIES)
    .map((q) => globalThis.matchMedia?.(q))
    .filter((l): l is MediaQueryList => Boolean(l));
  for (const l of lists) l.addEventListener("change", onChange);
  return () => {
    for (const l of lists) l.removeEventListener("change", onChange);
  };
}

/** Snapshot as a primitive string so useSyncExternalStore can compare cheaply. */
function snapshot(): string {
  let surface: Surface = "mobile";
  if (matches(QUERIES.lg)) surface = "desktop";
  else if (matches(QUERIES.md)) surface = "tablet";
  return `${surface}|${matches(QUERIES.coarse) ? "touch" : "pointer"}`;
}

export interface Viewport {
  surface: Surface;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  /** Touch-first device (finger-sized targets, no hover). Independent of width. */
  isTouch: boolean;
}

export function useViewport(): Viewport {
  const [surface, input] = useSyncExternalStore(subscribe, snapshot, snapshot).split("|") as [Surface, string];
  return {
    surface,
    isMobile: surface === "mobile",
    isTablet: surface === "tablet",
    isDesktop: surface === "desktop",
    isTouch: input === "touch",
  };
}
